import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Trash2, Edit2, ClipboardList } from 'lucide-react';
import { vendorRiskApi, Vendor, VendorStats } from '../api/vendorRisk.api';

const riskLevelBadge: Record<string, string> = {
  low: 'bg-green-100 text-green-800',
  medium: 'bg-yellow-100 text-yellow-800',
  high: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
};

const statusBadge: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  under_review: 'bg-yellow-100 text-yellow-800',
  terminated: 'bg-red-100 text-red-800',
  pending: 'bg-gray-100 text-gray-800',
};

const EMPTY_FORM = {
  name: '',
  service_category: '',
  description: '',
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  website: '',
  likelihood: 3,
  impact: 3,
  contract_renewal_date: '',
  notes: '',
};

const EMPTY_ASSESSMENT = {
  likelihood: 3,
  impact: 3,
  findings: '',
  recommendations: '',
  next_assessment_date: '',
};

const VendorRegister: React.FC = () => {
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [stats, setStats] = useState<VendorStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const [statusFilter, setStatusFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [search, setSearch] = useState('');

  const [vendorModal, setVendorModal] = useState<{ open: boolean; editing: Vendor | null }>({ open: false, editing: null });
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  const [assessmentModal, setAssessmentModal] = useState<{ open: boolean; vendor: Vendor | null }>({ open: false, vendor: null });
  const [assessForm, setAssessForm] = useState(EMPTY_ASSESSMENT);

  const [deleteConfirm, setDeleteConfirm] = useState<Vendor | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const [list, statsRes] = await Promise.all([
        vendorRiskApi.getVendors({
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(riskFilter ? { risk_level: riskFilter } : {}),
          ...(search ? { search } : {}),
          limit: 100,
        }),
        vendorRiskApi.getVendorStats(),
      ]);
      setVendors(list.vendors || []);
      setStats(statsRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, riskFilter, search]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setVendorModal({ open: true, editing: null });
  };

  const openEdit = (v: Vendor) => {
    setForm({
      name: v.name,
      service_category: v.service_category,
      description: v.description || '',
      contact_name: v.contact_name || '',
      contact_email: v.contact_email || '',
      contact_phone: v.contact_phone || '',
      website: v.website || '',
      likelihood: 3,
      impact: 3,
      contract_renewal_date: v.contract_renewal_date || '',
      notes: v.notes || '',
    });
    setVendorModal({ open: true, editing: v });
  };

  const handleSaveVendor = async () => {
    if (!form.name.trim() || !form.service_category.trim()) return;
    setSubmitting(true);
    try {
      const payload: Partial<Vendor> = {
        name: form.name,
        service_category: form.service_category,
        ...(form.description && { description: form.description }),
        ...(form.contact_name && { contact_name: form.contact_name }),
        ...(form.contact_email && { contact_email: form.contact_email }),
        ...(form.contact_phone && { contact_phone: form.contact_phone }),
        ...(form.website && { website: form.website }),
        ...(form.contract_renewal_date && { contract_renewal_date: form.contract_renewal_date }),
        ...(form.notes && { notes: form.notes }),
      };
      if (vendorModal.editing) {
        await vendorRiskApi.updateVendor(vendorModal.editing.id, payload);
      } else {
        await vendorRiskApi.createVendor(payload);
      }
      setVendorModal({ open: false, editing: null });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (vendor: Vendor) => {
    setSubmitting(true);
    try {
      await vendorRiskApi.deleteVendor(vendor.id);
      setDeleteConfirm(null);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAssessment = async () => {
    if (!assessmentModal.vendor) return;
    setSubmitting(true);
    try {
      await vendorRiskApi.createAssessment(assessmentModal.vendor.id, {
        likelihood: assessForm.likelihood,
        impact: assessForm.impact,
        ...(assessForm.findings && { findings: assessForm.findings }),
        ...(assessForm.recommendations && { recommendations: assessForm.recommendations }),
        ...(assessForm.next_assessment_date && { next_assessment_date: assessForm.next_assessment_date }),
      });
      setAssessmentModal({ open: false, vendor: null });
      setAssessForm(EMPTY_ASSESSMENT);
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const riskScore = form.likelihood * form.impact;
  const assessScore = assessForm.likelihood * assessForm.impact;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Vendor Register</h1>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          <button onClick={openCreate} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> Add Vendor
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Vendors', value: stats.total, color: 'text-gray-900' },
            { label: 'High Risk', value: stats.high_risk, color: 'text-orange-600' },
            { label: 'Critical Risk', value: stats.critical_risk, color: 'text-red-600' },
            { label: 'Due for Review', value: stats.due_for_review, color: 'text-yellow-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {['', 'active', 'under_review', 'terminated', 'pending'].map(s => (
            <option key={s} value={s}>{s ? s.replace('_', ' ') : 'All Statuses'}</option>
          ))}
        </select>
        <select value={riskFilter} onChange={e => setRiskFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
          {['', 'low', 'medium', 'high', 'critical'].map(r => (
            <option key={r} value={r}>{r ? r : 'All Risk Levels'}</option>
          ))}
        </select>
        <input
          type="text"
          placeholder="Search vendors..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-48"
        />
      </div>

      <div className="bg-white border rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
        ) : vendors.length === 0 ? (
          <div className="py-12 text-gray-500">No vendors found.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                {['Name', 'Category', 'Risk Level', 'Status', 'Contact', 'Last Assessed', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {vendors.map(v => (
                <tr key={v.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-left font-medium text-gray-900">{v.name}</td>
                  <td className="px-4 py-3 text-left capitalize text-gray-600">{v.service_category}</td>
                  <td className="px-4 py-3 text-left">
                    {v.risk_level ? (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${riskLevelBadge[v.risk_level] || 'bg-gray-100 text-gray-700'}`}>
                        {v.risk_level}
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-left">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge[v.status] || 'bg-gray-100 text-gray-700'}`}>
                      {v.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-left text-gray-600">
                    {v.contact_name && <div>{v.contact_name}</div>}
                    {v.contact_email && <div className="text-xs text-gray-400">{v.contact_email}</div>}
                    {!v.contact_name && !v.contact_email && <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-left text-gray-600">
                    {v.last_assessed ? new Date(v.last_assessed).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : <span className="text-gray-400">Never</span>}
                  </td>
                  <td className="px-4 py-3 text-left">
                    <div className="flex items-center gap-2">
                      <button onClick={() => openEdit(v)} title="Edit" className="text-gray-400 hover:text-blue-600">
                        <Edit2 size={15} />
                      </button>
                      <button onClick={() => { setAssessmentModal({ open: true, vendor: v }); setAssessForm(EMPTY_ASSESSMENT); }} title="Add Assessment" className="text-gray-400 hover:text-green-600">
                        <ClipboardList size={15} />
                      </button>
                      <button onClick={() => setDeleteConfirm(v)} title="Delete" className="text-gray-400 hover:text-red-600">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create / Edit Vendor Modal */}
      {vendorModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl space-y-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-semibold">{vendorModal.editing ? 'Edit Vendor' : 'Add Vendor'}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="text-xs text-gray-500 block mb-1">Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="Vendor name" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500 block mb-1">Category *</label>
                <input value={form.service_category} onChange={e => setForm({ ...form, service_category: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="e.g. Cloud Services, Legal, HR" />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-gray-500 block mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Contact Name</label>
                <input value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Contact Email</label>
                <input type="email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Contact Phone</label>
                <input value={form.contact_phone} onChange={e => setForm({ ...form, contact_phone: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Website</label>
                <input value={form.website} onChange={e => setForm({ ...form, website: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm" placeholder="https://" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Likelihood (1-5): <strong>{form.likelihood}</strong></label>
                <input type="range" min={1} max={5} value={form.likelihood}
                  onChange={e => setForm({ ...form, likelihood: Number(e.target.value) })} className="w-full" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Impact (1-5): <strong>{form.impact}</strong></label>
                <input type="range" min={1} max={5} value={form.impact}
                  onChange={e => setForm({ ...form, impact: Number(e.target.value) })} className="w-full" />
              </div>
              <div className="col-span-2">
                <p className="text-sm">Risk Score: <span className={`px-2 py-0.5 rounded font-bold text-xs ${riskScore >= 20 ? 'bg-red-100 text-red-800' : riskScore >= 12 ? 'bg-orange-100 text-orange-800' : riskScore >= 6 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{riskScore}</span></p>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Next Review Date</label>
                <input type="date" value={form.contract_renewal_date}
                  onChange={e => setForm({ ...form, contract_renewal_date: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex gap-3 justify-end pt-2">
              <button onClick={() => setVendorModal({ open: false, editing: null })} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleSaveVendor} disabled={submitting || !form.name.trim() || !form.service_category.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving...' : vendorModal.editing ? 'Save Changes' : 'Add Vendor'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Modal */}
      {assessmentModal.open && assessmentModal.vendor && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg space-y-4">
            <h2 className="text-lg font-semibold">Add Assessment — {assessmentModal.vendor.name}</h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Likelihood (1-5): <strong>{assessForm.likelihood}</strong></label>
                <input type="range" min={1} max={5} value={assessForm.likelihood}
                  onChange={e => setAssessForm({ ...assessForm, likelihood: Number(e.target.value) })} className="w-full" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Impact (1-5): <strong>{assessForm.impact}</strong></label>
                <input type="range" min={1} max={5} value={assessForm.impact}
                  onChange={e => setAssessForm({ ...assessForm, impact: Number(e.target.value) })} className="w-full" />
              </div>
            </div>
            <p className="text-sm">Risk Score: <span className={`px-2 py-0.5 rounded font-bold text-xs ${assessScore >= 20 ? 'bg-red-100 text-red-800' : assessScore >= 12 ? 'bg-orange-100 text-orange-800' : assessScore >= 6 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>{assessScore}</span></p>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Findings</label>
              <textarea value={assessForm.findings} onChange={e => setAssessForm({ ...assessForm, findings: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Recommendations</label>
              <textarea value={assessForm.recommendations} onChange={e => setAssessForm({ ...assessForm, recommendations: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Next Assessment Date</label>
              <input type="date" value={assessForm.next_assessment_date}
                onChange={e => setAssessForm({ ...assessForm, next_assessment_date: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setAssessmentModal({ open: false, vendor: null })} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleSaveAssessment} disabled={submitting}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving...' : 'Save Assessment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm space-y-4">
            <h2 className="text-lg font-semibold">Delete Vendor</h2>
            <p className="text-sm text-gray-600">Are you sure you want to delete <strong>{deleteConfirm.name}</strong>? This cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteConfirm(null)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} disabled={submitting}
                className="px-4 py-2 text-sm text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50">
                {submitting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorRegister;
